import type { Artwork } from '../types';

export type Screen = 'intro' | 'loading' | 'scanning' | 'playing' | 'error' | 'unsupported';

const UNSUPPORTED_TEXT = {
  inapp: 'このブラウザではカメラを使えません。右上のメニューから「Safari で開く」または「Chrome で開く」を選ぶか、リンクをコピーしてブラウザで開いてください。',
  unsupported: 'このブラウザはカメラに対応していません。Safari または Chrome で開いてください。',
  denied: 'カメラの使用が許可されていません。ブラウザの設定でこのサイトのカメラを許可してから、もう一度開いてください。',
  notfound: '使用できるカメラが見つかりませんでした。',
} as const;

function $<T extends HTMLElement>(root: Document, id: string): T {
  const el = root.getElementById(id);
  if (!el) throw new Error(`#${id} missing`);
  return el as T;
}

export class Screens {
  readonly video: HTMLVideoElement;
  readonly startButton: HTMLButtonElement;
  readonly retryButton: HTMLButtonElement;
  readonly overlayButton: HTMLButtonElement;
  readonly copyButton: HTMLButtonElement;
  readonly muteButton: HTMLButtonElement;
  private readonly sections: Record<Exclude<Screen, 'scanning' | 'playing'>, HTMLElement>;
  private readonly guide: HTMLElement;
  private readonly card: HTMLElement;
  private readonly overlay: HTMLElement;
  private readonly progressLabel: HTMLElement;
  private readonly progressFill: HTMLElement;
  private readonly errorMessage: HTMLElement;
  private readonly unsupportedMessage: HTMLElement;
  private readonly introTitle: HTMLElement;
  private readonly introArtist: HTMLElement;

  constructor(root: Document) {
    this.video = $(root, 'video');
    this.startButton = $(root, 'start');
    this.retryButton = $(root, 'retry');
    this.overlayButton = $(root, 'resume');
    this.copyButton = $(root, 'copy-link');
    this.muteButton = $(root, 'mute');
    this.sections = {
      intro: $(root, 'screen-intro'),
      loading: $(root, 'screen-loading'),
      error: $(root, 'screen-error'),
      unsupported: $(root, 'screen-unsupported'),
    };
    this.guide = $(root, 'guide');
    this.card = $(root, 'card');
    this.overlay = $(root, 'overlay-resume');
    this.progressLabel = $(root, 'progress-label');
    this.progressFill = $(root, 'progress-fill');
    this.errorMessage = $(root, 'error-message');
    this.unsupportedMessage = $(root, 'unsupported-message');
    this.introTitle = $(root, 'intro-title');
    this.introArtist = $(root, 'intro-artist');
  }

  show(s: Screen): void {
    for (const [name, el] of Object.entries(this.sections)) el.hidden = name !== s;
    this.guide.hidden = s !== 'scanning';
    this.card.hidden = s !== 'playing';
    this.muteButton.hidden = s !== 'scanning' && s !== 'playing';
  }

  /** 消音ボタンの表示。状態そのものは AudioPlayer が持つ。 */
  setMuted(muted: boolean): void {
    this.muteButton.textContent = muted ? '🔇' : '🔊';
    this.muteButton.setAttribute('aria-label', muted ? '消音を解除' : '消音');
    this.muteButton.classList.toggle('muted', muted);
  }

  setIntro(a: Artwork): void {
    this.introTitle.textContent = a.title;
    this.introArtist.textContent = a.artist;
  }

  setProgress(label: string, fraction: number | null): void {
    this.progressLabel.textContent = label;
    this.progressFill.classList.toggle('indeterminate', fraction === null);
    if (fraction !== null) this.progressFill.style.width = `${Math.round(fraction * 100)}%`;
  }

  setError(message: string): void {
    this.errorMessage.textContent = message;
    this.show('error');
  }

  setUnsupported(kind: keyof typeof UNSUPPORTED_TEXT, url: string): void {
    this.unsupportedMessage.textContent = UNSUPPORTED_TEXT[kind];
    this.copyButton.hidden = kind !== 'inapp';
    this.copyButton.onclick = () => {
      void navigator.clipboard?.writeText(url).then(() => (this.copyButton.textContent = 'コピーしました'));
    };
    this.show('unsupported');
  }

  setOverlay(visible: boolean): void {
    this.overlay.hidden = !visible;
  }

  get cardElement(): HTMLElement {
    return this.card;
  }
}
